<template>
  <div>
    <v-data-table
      :headers="headers"
      :items="classes"
      class="elevation-1"
      hide-default-footer
    >
      <template v-slot:top>
        <v-toolbar
          flat
        >
          <v-spacer />
          <v-btn
            color="secondary"
            :dark="hasValidEdition && hasValidInscription && !hasPaid"
            class="mb-2"
            :disabled="!hasValidEdition || !hasValidInscription || hasPaid"
            @click="createClass"
          >
            Ajouter une classe
          </v-btn>

          <v-dialog
            v-model="dialogDelete"
            max-width="500px"
          >
            <v-card>
              <v-card-title class="text-h5">
                Are you sure you want to delete this item?
              </v-card-title>
              <v-card-actions>
                <v-spacer />
                <v-btn
                  color="blue darken-1"
                  text
                  @click="closeDelete"
                >
                  Cancel
                </v-btn>
                <v-btn
                  color="blue darken-1"
                  text
                  @click="deleteItemConfirm"
                >
                  OK
                </v-btn>
                <v-spacer />
              </v-card-actions>
            </v-card>
          </v-dialog>
        </v-toolbar>
      </template>

      <template v-slot:item.doSingulis="{ item }">
        <v-icon
          v-if="item.doSingulis"
          color="green"
        >
          mdi-check-circle
        </v-icon>
        <v-icon
          v-if="!item.doSingulis"
          color="grey"
        >
          mdi-minus-circle-outline
        </v-icon>
      </template>

      <template v-slot:item.doOmnibus="{ item }">
        <v-icon
          v-if="item.doOmnibus"
          color="green"
        >
          mdi-check-circle
        </v-icon>
        <v-icon
          v-if="!item.doOmnibus"
          color="grey"
        >
          mdi-minus-circle-outline
        </v-icon>
      </template>

      <template v-slot:item.actions="{ item }">
        <v-btn
          fab
          class="ma-2"
          @click="editItem(item)"
        >
          <v-icon>mdi-pencil</v-icon>
        </v-btn>
      </template>

      <template v-slot:no-data>
        <span v-if="!hasValidInscription">
          Veuillez vous inscrire à l'édition en cours pour ajouter une classe
        </span>
        <span v-else>
          Pas de classe encore inscrite
        </span>
      </template>
    </v-data-table>

    <v-dialog
      v-model="showEdit"
      max-width="550px"
    >
      <v-card>
        <v-card-title>
          <span class="text-h5">{{ formTitle }}</span>
        </v-card-title>

        <v-card-text>
          <v-container v-if="editedItem">
            <v-row>
              <v-col
                cols="12"
                md="8"
              >
                <v-text-field
                  v-model="editedItem.name"
                  label="Nom de la classe"
                />
              </v-col>
              <v-col
                cols="12"
                md="4"
              >
                <v-text-field
                  v-model="editedItem.nbStudents"
                  :disabled="hasPaid"
                  label="Nombre d'élèves"
                  type="number"
                />
              </v-col>
            </v-row>
            <div class="d-flex align-center">
              <v-switch v-model="editedItem.doSingulis" />
              <div class="text-h6 ml-5">
                Participera au Singulis
              </div>
            </div>
            <div class="d-flex align-center">
              <v-switch v-model="editedItem.doOmnibus" />
              <div class="text-h6 ml-5">
                Participera à l'Omnibus
              </div>
            </div>
            <v-divider />
            <div v-if="editedItem.doSingulis">
              <br>
              <v-menu
                ref="menu"
                v-model="showDatePicker"
                :close-on-content-click="false"
                :return-value.sync="editedItem.singulisDate"
                transition="scale-transition"
                offset-y
                min-width="auto"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    v-model="editedItem.singulisDate"
                    label="Pour Singulis, à quelle date allez-vous passer l'épreuve ?"
                    prepend-icon="mdi-calendar"
                    readonly
                    v-bind="attrs"
                    v-on="on"
                  />
                </template>
                <v-date-picker
                  v-model="editedItem.singulisDate"
                  :min="singulisContestStartDate"
                  :max="singulisContestEndDate"
                  no-title
                  scrollable
                  local="fr"
                >
                  <v-spacer />
                  <v-btn
                    text
                    color="primary"
                    @click="showDatePicker = false"
                  >
                    Annuler
                  </v-btn>
                  <v-btn
                    text
                    color="primary"
                    @click="$refs.menu.save(editedItem.singulisDate)"
                  >
                    OK
                  </v-btn>
                </v-date-picker>
              </v-menu>
            </div>
          </v-container>

          <v-alert
            v-if="hasError"
            dense
            outlined
            type="error"
            class="ma-3"
          >
            {{ hasError }}
          </v-alert>
        </v-card-text>

        <v-card-actions>
          <v-btn
            text
            @click="showEdit = false"
          >
            Annuler
          </v-btn>
          <v-spacer />
          <v-btn
            color="primary"
            :loading="isSaving"
            :disabled="isSaving"
            @click="updateClass"
          >
            Confirmer
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
  import { get, call } from 'vuex-pathify'
  export default {
    data: () => ({
      hasError: '',
      showDatePicker: null,
      showEdit: false,
      isSaving: false,
      dialog: false,
      dialogDelete: false,
      headers: [
        {
          text: 'Classe',
          align: 'start',
          sortable: false,
          value: 'name',
        },
        { text: 'Nombre d\'élève', value: 'nbStudents', align: 'center', sortable: false },
        { text: 'Singulis', value: 'doSingulis', align: 'center', sortable: false },
        { text: 'Omnibus', value: 'doOmnibus', align: 'center', sortable: false },
        { text: 'Actions', value: 'actions', align: 'right', sortable: false },
      ],
      editedIndex: -1,
      editedItem: null,
      defaultItem: {
        name: '',
        nbStudents: 0,
        doSingulis: false,
        doOmnibus: false,
        singulisDate: null,
      },
    }),

    computed: {
      hasValidEdition: get('artifex/config.hasValidEdition'),
      hasValidInscription: get('artifex/inscription.isValid'),
      hasPaid: get('artifex/inscription.hasPaid'),
      singulisContestStart: get('artifex/config.edition.singulisContestStart'),
      singulisContestEnd: get('artifex/config.edition.singulisContestEnd'),
      classes: get('artifex/classes'),
      formTitle () {
        return this.editedIndex === -1 ? 'Ajouter une classe' : 'Modifier la classe'
      },
      singulisContestStartDate () {
        return this.singulisContestStart.toISOString().split('T')[0]
      },
      singulisContestEndDate () {
        return this.singulisContestEnd.toISOString().split('T')[0]
      },
    },

    watch: {
      showEdit (val) {
        if (!val) {
          this.hasError = ''
        }
      },
      dialogDelete (val) {
        val || this.closeDelete()
      },
    },

    methods: {
      loadClasses: call('artifex/loadClasses'),

      createClass () {
        this.showEdit = true
        this.editedItem = Object.assign({}, this.defaultItem)
        if (!this.editedItem.singulisDate || this.editedItem.singulisDate < this.singulisContestStart) {
          this.editedItem.singulisDate = this.singulisContestStartDate
        }
      },

      editItem (item) {
        this.showEdit = true
        this.editedItem = Object.assign({}, item)
        if (!this.editedItem.singulisDate || this.editedItem.singulisDate < this.singulisContestStart) {
          this.editedItem.singulisDate = this.singulisContestStartDate
        } else {
          this.editedItem.singulisDate = item.singulisDate.toISOString().split('T')[0]
        }
      },

      deleteItem (item) {
        this.editedIndex = this.desserts.indexOf(item)
        this.editedItem = Object.assign({}, item)
        this.dialogDelete = true
      },

      deleteItemConfirm () {
        this.desserts.splice(this.editedIndex, 1)
        this.closeDelete()
      },

      close () {
        this.showEdit = false
        this.$nextTick(() => {
          this.editedItem = Object.assign({}, this.defaultItem)
          this.editedIndex = -1
        })
      },

      closeDelete () {
        this.dialogDelete = false
        this.$nextTick(() => {
          this.editedItem = Object.assign({}, this.defaultItem)
          this.editedIndex = -1
        })
      },

      async updateClass () {
        const toSave = Object.assign({}, this.editedItem)
        toSave.nbStudents = parseInt(toSave.nbStudents)
        toSave.singulisDate = new Date(toSave.singulisDate)
        console.log(toSave)
        this.isSaving = true
        try {
          await this.$lco.participant.registerClass(toSave)
          await this.loadClasses()
          this.isSaving = false
          this.showEdit = false
        } catch (err) {
          this.hasError = 'Impossible d\'enregistrer la classe pour le moment. Assurez vous que son nom et le nombre d`élèves sont bien définis ou recommencez un peu plus tard'
          console.error(err)
          this.isSaving = false
        }
      },

      save () {
        if (this.editedIndex > -1) {
          Object.assign(this.desserts[this.editedIndex], this.editedItem)
        } else {
          this.desserts.push(this.editedItem)
        }
        this.close()
      },
    },
  }
</script>
